"use client";

import { FormEvent, useMemo, useState } from "react";
import { BUILT_SYSTEMS, SERVICES } from "@/lib/content";
import { PreferredDateTimePicker } from "@/components/PreferredDateTimePicker";

type Props = {
  defaultService?: string;
  intent?: string;
  defaultTab?: "inquiry" | "consultation";
};

const SERVICE_OPTIONS = [
  ...BUILT_SYSTEMS.map((s) => s.title),
  ...SERVICES.map((s) => s.title),
];

type Tab = "inquiry" | "consultation";

export function LeadForm({
  defaultService = "",
  intent = "contact",
  defaultTab = "inquiry",
}: Props) {
  const initialTab = useMemo<Tab>(() => {
    if (intent === "consultation" || defaultTab === "consultation") {
      return "consultation";
    }
    return "inquiry";
  }, [intent, defaultTab]);

  const [tab, setTab] = useState<Tab>(initialTab);
  const [status, setStatus] = useState<"idle" | "busy" | "ok" | "err">("idle");
  const [message, setMessage] = useState("");
  const [preferredAt, setPreferredAt] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setStatus("busy");
    setMessage("");

    const isConsultation = tab === "consultation";
    const endpoint = isConsultation ? "/api/consultations" : "/api/leads";

    try {
      const payload: Record<string, unknown> = {
        name: fd.get("name"),
        email: fd.get("email"),
        phone: fd.get("phone") || undefined,
        company: fd.get("company") || undefined,
        message: fd.get("message") || undefined,
        page: typeof window !== "undefined" ? window.location.pathname : "/contact",
        referrer: typeof document !== "undefined" ? document.referrer : undefined,
        website: fd.get("website") || "",
      };

      if (isConsultation) {
        payload.service = fd.get("service") || undefined;
        payload.preferredAt = preferredAt || undefined;
      } else {
        payload.serviceInterest = fd.get("serviceInterest") || undefined;
        payload.intent = intent === "quote" ? "quote" : "contact";
        payload.utmSource =
          new URLSearchParams(window.location.search).get("utm_source") || undefined;
        payload.utmMedium =
          new URLSearchParams(window.location.search).get("utm_medium") || undefined;
        payload.utmCampaign =
          new URLSearchParams(window.location.search).get("utm_campaign") || undefined;
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(body.error || "Failed");
      setStatus("ok");
      setMessage(
        isConsultation
          ? "Thanks — your consultation request is in. The right specialist will follow up soon."
          : "Thanks — we received your message and will follow up soon.",
      );
      form.reset();
      setPreferredAt("");
      if (typeof window !== "undefined" && "umami" in window) {
        (window as { umami?: { track: (n: string) => void } }).umami?.track(
          isConsultation ? "consultation_submit" : "lead_submit",
        );
      }
    } catch {
      setStatus("err");
      setMessage("Could not send right now. Please try again or email us directly.");
    }
  }

  const isQuote = intent === "quote" && tab === "inquiry";

  return (
    <div className="lead-card">
      <div className="lead-tabs" role="tablist" aria-label="Contact form type">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "inquiry"}
          className={"lead-tab" + (tab === "inquiry" ? " is-active" : "")}
          onClick={() => {
            setTab("inquiry");
            setStatus("idle");
            setMessage("");
            setPreferredAt("");
          }}
        >
          Inquiry
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "consultation"}
          className={"lead-tab" + (tab === "consultation" ? " is-active" : "")}
          onClick={() => {
            setTab("consultation");
            setStatus("idle");
            setMessage("");
          }}
        >
          Consultation
        </button>
      </div>

      <form key={tab} onSubmit={onSubmit}>
        <div className="lead-card-head">
          <div className="mono-label">
            {tab === "consultation"
              ? "Book a consultation"
              : isQuote
                ? "Quote request"
                : "Inquiry"}
          </div>
          <p className="lead-card-intro">
            {tab === "consultation"
              ? "Pick a service and we will route it to the specialist who handles that work."
              : "A short note is enough — we will come back with a practical path."}
          </p>
        </div>

        <div className="lead-fields">
          <label>
            Name
            <input name="name" required autoComplete="off" />
          </label>
          <label>
            Email
            <input name="email" type="email" required autoComplete="off" />
          </label>
          <label>
            Phone
            <input name="phone" autoComplete="off" />
          </label>
          <label>
            Company
            <input name="company" autoComplete="off" />
          </label>

          {tab === "consultation" ? (
            <>
              <label className="lead-span">
                Service
                <select name="service" required defaultValue={defaultService}>
                  <option value="">— Select —</option>
                  {SERVICE_OPTIONS.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
              </label>
              <div className="lead-span pref-datetime-field">
                <span className="pref-datetime-field-label">Preferred date &amp; time</span>
                <PreferredDateTimePicker
                  value={preferredAt}
                  onChange={setPreferredAt}
                />
              </div>
              <label className="lead-span">
                Message
                <textarea name="message" rows={4} />
              </label>
            </>
          ) : (
            <>
              <label className="lead-span">
                Service interest
                <select name="serviceInterest" defaultValue={defaultService}>
                  <option value="">— Select —</option>
                  {SERVICE_OPTIONS.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="lead-span">
                Message
                <textarea name="message" rows={5} required />
              </label>
            </>
          )}
        </div>

        <label className="hp" aria-hidden>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>

        <button className="btn btn-primary" type="submit" disabled={status === "busy"}>
          {status === "busy"
            ? "Sending…"
            : tab === "consultation"
              ? "Book consultation"
              : isQuote
                ? "Request quote"
                : "Send message"}
        </button>
        {message && (
          <p className={"form-note " + (status === "ok" ? "ok" : "err")}>{message}</p>
        )}
      </form>
    </div>
  );
}
